/**
 * Where uploads wait: the queue's jobs, and the legs handed on to the service
 * worker. One IndexedDB database that the page and the worker both open --
 * which is the only way the two can see each other's uploads -- and, where
 * IndexedDB is refused, memory, so the page's queue still works there.
 *
 * No window here: the service worker imports this too.
 */
import type { Leg } from './uploadLegs';

const DB_NAME = 'givisnap-uploads';
// 2 added the legs. Opening at 1 would fail once a phone has opened 2.
const DB_VERSION = 2;
const JOBS = 'jobs';
const LEGS = 'legs';

/** A queued job, as far as storing it goes: the queue owns its shape. */
type Stored = { id: string };

let opening: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  opening ??= new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') return resolve(null);
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(JOBS)) db.createObjectStore(JOBS, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(LEGS)) db.createObjectStore(LEGS, { keyPath: 'jobId' });
    };
    request.onsuccess = () => resolve(request.result);
    // Private browsing, or storage refused. Memory stands in.
    request.onerror = () => resolve(null);
  });
  return opening;
}

/* Memory, for a phone that refuses IndexedDB, and the tests' jsdom. */
const memory = { jobs: new Map<string, Stored>(), legs: new Map<string, Leg>() };

/*
 * Settles only when the write has landed or definitely has not. A big clip can
 * be refused for room, which aborts the transaction; resolving then, or never
 * settling, told the queue a job was stored that was not -- and a job missing
 * from here reads as one the service worker finished.
 */
function write(store: string, change: (objects: IDBObjectStore) => void): Promise<void> {
  return openDb().then(
    (db) =>
      db &&
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('Could not save the upload.'));
        tx.onabort = () => reject(tx.error ?? new Error('Saving the upload was stopped.'));
        change(tx.objectStore(store));
      }),
  ) as Promise<void>;
}

function readAllOf<T>(store: string): Promise<T[] | null> {
  return openDb().then(
    (db) =>
      db &&
      new Promise<T[]>((resolve) => {
        const request = db.transaction(store, 'readonly').objectStore(store).getAll();
        request.onsuccess = () => resolve((request.result ?? []) as T[]);
        request.onerror = () => resolve([]);
      }),
  );
}

/** Whether uploads outlive the page: false where only memory is to hand. */
export async function lasting(): Promise<boolean> {
  return (await openDb()) !== null;
}

export async function saveJob(job: Stored): Promise<void> {
  if (await lasting()) return write(JOBS, (jobs) => jobs.put(job));
  memory.jobs.set(job.id, job);
}

export async function forgetJob(id: string): Promise<void> {
  if (await lasting()) return write(JOBS, (jobs) => jobs.delete(id));
  memory.jobs.delete(id);
}

export async function allJobs<T extends Stored>(): Promise<T[]> {
  const stored = await readAllOf<T>(JOBS);
  return stored ?? ([...memory.jobs.values()] as T[]);
}

export async function hasJob(id: string): Promise<boolean> {
  return (await allJobs()).some((job) => job.id === id);
}

export async function saveLeg(leg: Leg): Promise<void> {
  if (await lasting()) return write(LEGS, (legs) => legs.put(leg));
  memory.legs.set(leg.jobId, leg);
}

export async function forgetLeg(jobId: string): Promise<void> {
  if (await lasting()) return write(LEGS, (legs) => legs.delete(jobId));
  memory.legs.delete(jobId);
}

export async function allLegs(): Promise<Leg[]> {
  const stored = await readAllOf<Leg>(LEGS);
  return stored ?? [...memory.legs.values()];
}

export async function legOf(jobId: string): Promise<Leg | undefined> {
  return (await allLegs()).find((leg) => leg.jobId === jobId);
}
